import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import AsyncSelect from "react-select/async";
import { toast } from '../../Library/SubMenus/MCQAssessment/toastUtils';
import axiosInstance from "../../../interceptor/axiosInstance";

const AssignBulk = ({ onClose }) => {
  const [selectedValues, setSelectedValues] = useState({
    program: null,
    semester: null,
    department: null,
    designation: null,
  });

  const [selectedSeries, setSelectedSeries] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const initialized = useRef(false);

  useEffect(() => {
    initialized.current = true;
  }, []);

  // Generic async dropdown fetcher
  const fetchOptions = async (type, inputValue) => {
    try {
      const response = await axiosInstance.get(`programs/details/?name=${inputValue}`);
      const data = response.data?.data || {};
      const options = data[type] || [];

      return options.map((item) => ({
        value: item.name,
        label: item.name,
      }));
    } catch (error) {
      return [];
    }
  };

  // Fetch assessments with search input
  const fetchAssessmentOptions = async (inputValue = "") => {
    try {
      const response = await axiosInstance.get(`assessments/tests/`, {
        params: {
          name: inputValue,
          pageSize: 20,
        },
      });

      const results = response.data?.data?.results || [];

      return results.map((item) => ({
        value: item.id,
        label: item.name,
      }));
    } catch (error) {
      return [];
    }
  };

  const handleSubmit = () => {
    // Validation
    if (!selectedSeries) {
      toast.error('Validation Error', 'Please select an assessment.');
      return;
    }

    setSubmitting(true);
    const requestBody = {
      test_id: selectedSeries?.value || "",
      program: selectedValues.program?.value || "",
      semester: selectedValues.semester?.value || "",
      department: selectedValues.department?.value || "",
      designation: selectedValues.designation?.value || "",
    };

    axiosInstance
      .post("assign/bulk-assign/", requestBody)
      .then((response) => {
        const { message, data } = response.data;
        let successMessage = message || "Users assigned successfully!";

        if (data) {
          const { created_users = 0, skipped_rows = 0, errors = [] } = data;
          successMessage += `\nCreated: ${created_users}, Skipped: ${skipped_rows}`;

          if (errors.length > 0) {
            console.warn("Bulk assign errors:", errors);
          }
        }

        toast.success("Bulk Assign Successful", successMessage);
        onClose();
      })
      .catch((error) => {
        console.error("Error submitting data:", error);
        let errorMessage = "Failed to assign users. Please try again.";

        if (error.response?.data) {
          const { message, errors = [] } = error.response.data;
          if (message) {
            errorMessage = Array.isArray(message)
              ? message.join(", ")
              : message;
          }

          if (errors.length > 0) {
            console.error("Bulk assign errors:", errors);
          }
        }

        toast.error("Assignment Failed", errorMessage);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return ReactDOM.createPortal(
    <div className="assign-bulk-modal-overlay">
      <div className="assign-bulk-modal-container">
        {/* Header */}
        <div className="assign-bulk-modal-header">
          <h2>📋 Bulk Assign Assessment</h2>
          <button 
            className="assign-bulk-modal-close" 
            onClick={onClose}
            aria-label="Close"
            disabled={submitting}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="assign-bulk-modal-content">
          <p className="assign-bulk-modal-description">
            Assign assessments to multiple users based on their program, semester, department, or designation.
          </p>

          <div className="assign-bulk-modal-form">
            {/* Program Dropdown */}
            <div className="assign-bulk-modal-field">
              <label className="assign-bulk-modal-label">Program</label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={(inputValue) => fetchOptions("programs", inputValue)}
                value={selectedValues.program}
                onChange={(option) =>
                  setSelectedValues({ ...selectedValues, program: option })
                }
                placeholder="Select Program (optional)"
                isDisabled={submitting}
                className="assign-bulk-modal-select"
                classNamePrefix="assign-bulk-select"
              />
            </div>

            {/* Semester Dropdown */}
            <div className="assign-bulk-modal-field">
              <label className="assign-bulk-modal-label">Semester</label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={(inputValue) => fetchOptions("semesters", inputValue)}
                value={selectedValues.semester}
                onChange={(option) =>
                  setSelectedValues({ ...selectedValues, semester: option })
                }
                placeholder="Select Semester (optional)"
                isDisabled={submitting}
                className="assign-bulk-modal-select"
                classNamePrefix="assign-bulk-select"
              />
            </div>

            {/* Department Dropdown */}
            <div className="assign-bulk-modal-field">
              <label className="assign-bulk-modal-label">Department</label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={(inputValue) => fetchOptions("departments", inputValue)}
                value={selectedValues.department}
                onChange={(option) =>
                  setSelectedValues({ ...selectedValues, department: option })
                }
                placeholder="Select Department (optional)"
                isDisabled={submitting}
                className="assign-bulk-modal-select"
                classNamePrefix="assign-bulk-select"
              />
            </div>

            {/* Designation Dropdown */}
            <div className="assign-bulk-modal-field">
              <label className="assign-bulk-modal-label">Designation</label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={(inputValue) => fetchOptions("designations", inputValue)}
                value={selectedValues.designation}
                onChange={(option) =>
                  setSelectedValues({ ...selectedValues, designation: option })
                }
                placeholder="Select Designation (optional)"
                isDisabled={submitting}
                className="assign-bulk-modal-select"
                classNamePrefix="assign-bulk-select"
              />
            </div>

            {/* Assessment Dropdown */}
            <div className="assign-bulk-modal-field assign-bulk-modal-field-required">
              <label className="assign-bulk-modal-label">
                Assessment <span className="assign-bulk-modal-required">*</span>
              </label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={fetchAssessmentOptions}
                value={selectedSeries}
                onChange={setSelectedSeries}
                placeholder="Search & Select Assessment"
                isDisabled={submitting}
                className="assign-bulk-modal-select"
                classNamePrefix="assign-bulk-select"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="assign-bulk-modal-footer">
          <button 
            className="assign-bulk-modal-cancel" 
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button 
            className="assign-bulk-modal-submit" 
            onClick={handleSubmit} 
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="assign-bulk-modal-spinner"></span>
                Submitting...
              </>
            ) : (
              'Submit Assignment'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AssignBulk;
